import type { AppType } from 'next/dist/shared/lib/utils';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { useRouter } from 'next/router';
import { isElectron } from '../common/electron';
import { ProgressProvider } from '../providers/progress-provider';
import { LocaleProvider } from '../contexts/locale-context';

// 'antd/dist/reset.css' 는 theme.css 의 `@import 'antd/dist/reset.css' layer(antd);` 로 layer 에 편입됨 (Modal footer 버튼 간격 회귀 대응)
import '../assets/styles/theme.css';
import '../assets/styles/global.css';
import '../assets/styles/antd.css';
import '../assets/styles/grid.css';
// import '../assets/styles/table.css';
import '../assets/styles/form.css';
import '../assets/styles/tabs.css';
import '../assets/styles/chart.css';
import '../assets/styles/modal.css';

// import '@ag-grid-community/styles/ag-grid.css';
// import '@ag-grid-community/styles/ag-theme-quartz.css';
import '../assets/styles/ag-grid.css';
import 'react-json-view-lite/dist/index.css';
import '@bit-box/chart-tools/dist/index.css';
import { setGlobalOptions } from '@bit-box/chart-tools';
import { priceFormatter } from '../common/format';

import '@bit-box/common/lib/array';
import '@bit-box/common/lib/date';
import '@bit-box/common/lib/map';
import '@bit-box/common/lib/string';
import '@bit-box/common/lib/number';

import type { PushMessage } from '@bit-box/common';
import { notification } from 'antd';
// ag-grid-community will always be implicitly available
import {
  ModuleRegistry,
  /* AllCommunityModule */
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  CsvExportModule,
  RowApiModule,
  ColumnApiModule,
  RenderApiModule,
  RowSelectionModule,
  RowStyleModule,
  CellStyleModule,
  GridStateModule,
  ExternalFilterModule,
  EventApiModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  QuickFilterModule,
  ValidationModule,
  TooltipModule,
  RowAutoHeightModule,
} from 'ag-grid-community';
import { BotLayout } from '../layouts/bot-layout';
import { EmptyLayout } from '../layouts/empty-layout';
import { DefaultLayout } from '../layouts/default-layout';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale.js';
// AntD DatePicker의 "월"단위에 한글이 표시되려면 dayjs locale 설정이 필수임.
import 'dayjs/locale/ko';

dayjs.locale('ko');
// locale 업데이트 지원
dayjs.extend(updateLocale);

// dayjs 기본값은 일요일(0)을 주의 시작으로 인식하는데,
// 바이낸스는 월요일(1)을 주의 시작으로 사용하므로 dayjs의 기본 locale을 업데이트
// dayjs의 기본 로케일이 'en'이라서 여기서 설정
dayjs.updateLocale('en', { weekStart: 1 });
dayjs.updateLocale('ko', { weekStart: 1 });

// chart-tools 글로벌 옵션 설정
setGlobalOptions({
  priceFormatter,
});

// AG Grid v30 부터는 모듈 방식으로 사용해야 함
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  CsvExportModule,
  RowApiModule,
  ColumnApiModule,
  RenderApiModule,
  RowSelectionModule,
  RowStyleModule,
  CellStyleModule,
  GridStateModule,
  ExternalFilterModule,
  EventApiModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  QuickFilterModule,
  ExternalFilterModule,
  ValidationModule,
  TooltipModule,
  RowAutoHeightModule,
]);
/**
 * SSR 비활성화를 위한 함수.
 * React 18에서 아래 방식이 안통해서 주석 처리함
 */
// function SafeHydrate({ children }: any) {
//   return <div suppressHydrationWarning>{typeof window === 'undefined' ? null : children}</div>;
// }

const MyApp: AppType = ({ Component, pageProps }) => {
  const [loaded, setLoaded] = useState(false);
  const router = useRouter();

  useEffect(() => setLoaded(true), []);

  // Electron에서 reload 이벤트 처리
  useEffect(() => {
    if (!isElectron || typeof window === 'undefined' || !window.electronAPI) {
      return undefined;
    }

    const cleanup = window.electronAPI.onPush((message: PushMessage) => {
      console.log('Push 이벤트 받음. 현재 경로:', router.pathname, message);

      // 현재 페이지 리로드
      if (message.cmd === 'reload') {
        router.push(`/redirect?to=${router.pathname}`);
      }

      if (message.cmd === 'exchangeError') {
        notification.error({
          message: '거래소 연결 실패',
          description: message.message,
          duration: 0, // 사용자가 직접 닫을 때까지 유지
        });
      }
    });

    // cleanup 함수 반환
    return cleanup;
  }, [router]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layouts: Record<string, any> = {
    DefaultLayout,
    EmptyLayout,
    BotLayout,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-type-assertion
  const Layout = layouts[(Component as any).layout as string] || DefaultLayout;

  // SSR 비활성화
  if (!loaded) {
    return null;
  }

  return (
    <LocaleProvider defaultLocale="ko">
      <ProgressProvider>
        <Layout>
          <Component {...pageProps} />
        </Layout>
      </ProgressProvider>
    </LocaleProvider>
  );
};

// Bot Id가 결정되기 전에 trpc를 사용할 수 없으므로 아래 withTRPC 주석 처리함
export default MyApp; // trpc.withTRPC(MyApp);
